// import-lines.ts — line import by paste: pure, tested, reusable parser (voice pipeline included).
//
// Two formats: SPREADSHEET (TSV / ";" / ",", recognised header or default order) and structured JSON.
//
// Round-trip: reads back our own amended export. Columns are recognised either as refs or as
// "REF — ITEM SHORT" labels: file name · combination ref · material · print · cut · lamination ·
// finishing 1 · finishing 2 · material supplier · brand · material supplied · L · H · number of files ·
// copies · production note · install note. The recognised headers are the FRENCH ones of the template
// and of the quote export, plus their English equivalents. NO price column is ever read (the engine
// recomputes them): if the paste contains any, they are ignored and reported.
//
// Supply migration: an old Excel quote carried a "material supplier" column that used to trigger the
// supplied-material purchase price mode (a mechanism since removed). Backward compatibility: a supplier
// entered without an explicit "material supplied" column turns the checkbox on
// (`materialSupplied=true`) — the supplier is kept in its column (informative) and the original mode is
// preserved. When the "material supplied" column is present it wins (recent, decoupled format) and the
// supplier is purely informative again.
//
// Gate check: every line is validated by the §17 matrix (itemAllowed). An item FORBIDDEN for the
// material is kept (never substituted) but the line is classified as incomplete, with a per-line
// reason — the engine will not price it. Unknown refs, prices and unreadable values become "ignored
// cells". Never a complete quote: only lines added to the current one.

import type { QuoteLine } from "../core/core";
import { CATALOGUE_VERSION } from "../core/version";
import { engineMessage, keyParam } from "../core/messages";
import { CATALOGUE } from "../../data";
import { combiComponents } from "../pricing/pa-margins";
import { itemAllowed } from "../catalogue/compatibility";
import { getItemBound } from "../catalogue/lookup";

/** An imported line = a draft line (no id/num, assigned by the caller). */
export type ImportLine = Partial<QuoteLine> & { type: QuoteLine["type"] };

/** A line classified as incomplete (forbidden combination) with its reasons. */
export interface IncompleteImportLine {
  /** 1-based row number in the paste (header excluded). */
  row: number;
  name: string;
  /** Reasons, in French (shown to the user). */
  reasons: string[];
}

/** Result of a paste import: the draft lines and the three-category report. */
export interface ImportResult {
  /** Created lines (clean and incomplete) — all added; incomplete ones will not be priced. */
  lines: ImportLine[];
  /** Number of lines without any issue. */
  cleanCount: number;
  /** Incomplete lines (forbidden combination) — reasons per line. */
  incomplete: IncompleteImportLine[];
  /** Ignored cells (French messages): price columns, unknown refs, unreadable values. */
  ignoredCells: string[];
  /** The paste contained price columns (recomputed by the tool). */
  pricesIgnored: boolean;
  format: "json" | "spreadsheet" | "empty";
  /** Empty source lines ignored silently. */
  ignoredRows: number;
  /** Template catalogue version detected in the paste (best-effort), otherwise null. */
  templateVersion: number | null;
  /** Warning (French) when the template is older than the current catalogue. */
  versionWarning: string | null;
  /**
   * Paste WITHOUT a header row whose columns do not line up: a message (French) explaining WHAT TO DO
   * (paste the header), never a blunt rejection. The shape is never guessed from the column count: a
   * wrong guess is paid in euros on a quote.
   */
  headerWarning: string | null;
}

/** Message for unrecognised columns: says what to do, and what works without a header (i18n key, shown to the user). */
const HEADER_MESSAGE = "engineImportHeaderUnrecognised";

/**
 * Normalises headers / keys: lowercase, accents stripped, "_" and "." to space, spaces collapsed.
 * @param s The header or key.
 * @returns The normalised form.
 */
const norm = (s: string) =>
  s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[_.]/g, " ").trim().replace(/\s+/g, " ");

/** Canonical column keys. Axis keys are the `QuoteLine` property names. */
type ColumnKey =
  | "fileName" | "comboRef" | "material" | "print" | "cut" | "lamination" | "finishing1" | "finishing2"
  | "designation" | "supplier" | "brand" | "materialSupplied" | "L" | "H" | "fileQty" | "copiesPerFile"
  | "prodNote" | "installNote" | "type";

/** Recognised header spellings per canonical key: French (template and export) first, then English. */
const HEADER_SPELLINGS: Record<ColumnKey, string[]> = {
  fileName: ["nom fichier", "nom de fichier", "fichier", "nom du fichier", "nomfichier", "file name", "filename", "file"],
  designation: ["designation", "desig", "libelle", "nom", "produit", "article", "label", "name"],
  comboRef: ["ref combi", "reference combi", "combi", "combinaison", "ref combinaison", "combiref", "combination", "combination ref", "combo ref"],
  material: ["matiere", "matiere / ref", "matiere/ref", "matiere ref", "ref", "reference", "material", "material / ref", "material/ref", "material ref"],
  print: ["print", "impression", "imp"],
  cut: ["coupe", "decoupe", "découpe", "cut", "cutting"],
  lamination: ["plastif", "plastification", "plf", "lamination"],
  finishing1: ["conf1", "conf 1", "confection 1", "confection", "finishing", "finishing 1", "finishing1"],
  finishing2: ["conf2", "conf 2", "confection 2", "finishing 2", "finishing2"],
  // Material supply: distributor (informative), manufacturer (informative), and the "material supplied"
  // checkbox (sole trigger of the supplied mode since the decoupling).
  supplier: ["fournmat", "fourn matiere", "fournisseur matiere", "fourn mat", "fournisseur", "distributeur", "material supplier", "supplier material", "supplier", "distributor", "matsupplier"],
  brand: ["marque", "fabricant", "brand", "manufacturer"],
  materialSupplied: ["matfournie", "matiere fournie", "fournie", "mat fournie", "material supplied", "supplied", "materialsupplied"],
  L: ["l", "largeur", "l (mm)", "l mm", "large", "width"],
  H: ["h", "hauteur", "h (mm)", "h mm", "haut", "height"],
  fileQty: ["nbf", "fichiers", "nb fichiers", "nbfichiers", "nombre de fichiers", "nb fich", "fich", "files", "nbfiles", "number of files", "nb files", "fileqty", "file qty"],
  copiesPerFile: ["qf", "exemplaires", "ex", "ex.", "quantite", "qte", "ex par fichier", "nbex", "copies", "quantity", "qty", "copies per file", "copiesperfile"],
  prodNote: ["note prod", "note production", "notes production", "noteprod", "note", "notes", "remarque", "production note", "prod note", "prodnote"],
  installNote: ["note pose", "notes pose", "notepose", "install note", "installation note", "installnote"],
  type: ["type"],
};

/** Recognised headers -> canonical key. */
const ALIAS: Record<string, ColumnKey> = (() => {
  const out: Record<string, ColumnKey> = {};
  for (const [key, spellings] of Object.entries(HEADER_SPELLINGS) as [ColumnKey, string[]][]) {
    for (const spelling of spellings) out[norm(spelling)] = key;
  }
  return out;
})();

/** Recognised price / computed columns -> ignored (recomputed by the engine) and reported. */
const PRICE_HEADERS = new Set(
  ["pv public", "rem agence", "pv net ag", "pv net ag.", "rem palier", "pv net pal", "pv net pal.", "rem %", "rem", "pv m2", "pv €/m²", "pv /m²", "pv m²",
    "pu theo", "pu théo", "pu mini", "pu def", "pu déf", "total", "% marge", "marge", "marge %", "marge €", "pa €/m²", "pa m2", "pa €/u", "pa u", "pa mat brut",
    "pa mat. brut", "chute %", "chute", "pa mat net", "pa mat. net", "prix", "montant", "pa total", "pv total",
    "waste %", "waste", "price", "amount"].map(norm),
);

/**
 * Default column order when no header is recognised (simple paste: ref, dimensions, quantities).
 * Does NOT cover the axis-column shape (material / print / cut / L / H / …): that one requires the
 * header row, which the template and the paste box placeholder always carry. Without a header, an
 * axis-column paste puts "IMP-1401" under L: `dimension()` REFUSES it, the line falls to incomplete
 * and lands in the report — never a dimension invented from a ref.
 */
const DEFAULT_COLUMN_ORDER: ColumnKey[] = ["designation", "L", "H", "fileQty", "copiesPerFile", "prodNote"];

/** Axis columns, in the order they are read. */
const AXES = ["material", "print", "cut", "lamination", "finishing1", "finishing2"] as const;
type Axis = (typeof AXES)[number];

/** i18n key of the axis name shown in ignored-cell messages. */
const AXIS_LABEL_KEY: Record<Axis, string> = {
  material: "engineImportAxisMaterial", print: "engineImportAxisPrint", cut: "engineImportAxisCut",
  lamination: "engineImportAxisLamination", finishing1: "engineImportAxisFinishing1", finishing2: "engineImportAxisFinishing2",
};

/** Catalogue item type expected under each axis (values of `catalog_items.type`). */
const ITEM_TYPE_BY_AXIS: Record<Axis, "material" | "print" | "cut" | "lamination" | "finishing"> = {
  material: "material", print: "print", cut: "cut", lamination: "lamination", finishing1: "finishing", finishing2: "finishing",
};

/**
 * Extracts the REF from a "REF — ITEM SHORT" or "[REF] SHORT" label, else the raw value (bare ref).
 * @param v The cell value.
 * @returns The uppercased ref.
 */
function refFromLabel(v: string): string {
  const s = v.trim();
  const bracket = s.match(/^\[([^\]]+)\]/);
  if (bracket) return bracket[1]!.trim().toUpperCase();
  const beforeDash = s.split(/\s+[—–]\s+/)[0]!; // before the em or en dash
  return beforeDash.trim().toUpperCase();
}

/**
 * Dimension integer (mm): "1 000 mm" -> "1000". Accepts a unit suffix (mm/cm/m) and REFUSES everything
 * else — notably an item ref ("IMP-1401") that a naive "first run of digits" would silently turn into
 * 1401. A refusal drops the line to incomplete, hence into the report: a shifted column must be noisy,
 * never become a plausible dimension.
 * @param v The cell value.
 * @returns The dimension as digits, or undefined when unreadable.
 */
function dimension(v: unknown): string | undefined {
  if (v == null) return undefined;
  const m = String(v).replace(/\s/g, "").match(/^(\d+)(?:[.,]0+)?(?:mm|cm|m)?$/i);
  return m ? m[1] : undefined;
}

/**
 * Positive integer (file count / copies). Falls back to the default when absent or invalid.
 * @param v The cell value.
 * @param fallback The value used when absent or invalid.
 * @returns The integer.
 */
function positiveInt(v: unknown, fallback = 1): number {
  if (v == null || String(v).trim() === "") return fallback;
  const n = parseInt(String(v).replace(/[^\d]/g, ""), 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

/**
 * Is the cell filled (neither empty nor blank)?
 * @param v The cell value.
 * @returns True when filled.
 */
const isFilled = (v: unknown) => v != null && String(v).trim() !== "";

/**
 * Explicitly negative tokens of the "material supplied" checkbox. A ticked box exports in a thousand
 * ways ("x", "oui", "true", "✓"): any other filled content is true.
 */
const NEGATIVE_SUPPLIED_TOKENS = new Set(["0", "false", "faux", "non", "n", "no", "off", "-", "—"]);

/**
 * "Material supplied" checkbox from a cell.
 * @param v The cell value.
 * @returns True unless the cell is empty or explicitly negative.
 */
const isSuppliedChecked = (v: unknown): boolean => {
  if (typeof v === "boolean") return v;
  const s = norm(String(v));
  return s !== "" && !NEGATIVE_SUPPLIED_TOKENS.has(s);
};

/**
 * Material refs never decompose into a combination: they feed the Material column only.
 * @param ref The uppercased ref.
 * @returns True when the ref is a catalogue material.
 */
const isMaterial = (ref: string) => CATALOGUE.some((m) => m.ref.toUpperCase() === ref);

/**
 * Resolves a designation / ref in STRICT order: a MATERIAL ref feeds the material alone (never decomposed
 * into a canonical combination); a REAL combination ref (distinct from a material, e.g. AD-101-LX) is
 * decomposed; otherwise free text becomes the file name. The combination ref column stays empty for a
 * material (no component to match) and is filled by reverse detection for a real combination.
 * @param raw The designation cell.
 * @returns The line fields it resolves to.
 */
function resolveDesignation(raw: string): Partial<QuoteLine> {
  const token = raw.trim();
  if (!token) return {};
  const ref = refFromLabel(token);
  if (isMaterial(ref)) return { material: ref };
  const combination = combiComponents(ref);
  if (combination) return { material: combination.material, print: combination.print, cut: combination.cut, lamination: combination.lamination, finishing1: combination.finishing1, finishing2: combination.finishing2 };
  return { fileName: token };
}

/** A record read from a paste row or a JSON object. */
type ImportRecord = Partial<Record<ColumnKey, unknown>>;

/**
 * Builds a line plus its diagnosis (incompatibility reasons, ignored cells) from a record.
 * @param rec The record.
 * @param row The 1-based row number.
 * @returns The line, its incompatibility reasons, its ignored cells and whether a dimension was unreadable.
 */
function toLine(rec: ImportRecord, row: number): { line: ImportLine; reasons: string[]; ignored: string[]; unreadableDimension: boolean } {
  const type = (rec.type ? String(rec.type).toUpperCase() : "PRINT_GF") as QuoteLine["type"];
  const l: ImportLine = { type, fileQty: positiveInt(rec.fileQty), copiesPerFile: positiveInt(rec.copiesPerFile) };
  const reasons: string[] = [];
  const ignored: string[] = [];
  let unreadableDimension = false;

  // Explicit axes (round-trip) win; otherwise the combination ref; otherwise the free designation.
  const hasAxes = AXES.some((a) => isFilled(rec[a]));
  if (hasAxes) {
    for (const axis of AXES) {
      if (!isFilled(rec[axis])) continue;
      const ref = refFromLabel(String(rec[axis]));
      const item = getItemBound(ref);
      if (!item || item.type !== ITEM_TYPE_BY_AXIS[axis]) {
        ignored.push(engineMessage("engineImportUnknownAxisRef", { row, ref, axis: keyParam(AXIS_LABEL_KEY[axis]) }));
        continue;
      }
      l[axis] = ref;
    }
  } else if (isFilled(rec.comboRef)) {
    const ref = refFromLabel(String(rec.comboRef));
    // A MATERIAL ref placed by mistake in the combination ref feeds the material, never the combination.
    if (isMaterial(ref)) {
      l.material = ref;
    } else {
      const combination = combiComponents(ref);
      if (combination) Object.assign(l, { material: combination.material, print: combination.print, cut: combination.cut, lamination: combination.lamination, finishing1: combination.finishing1, finishing2: combination.finishing2 });
      else ignored.push(engineMessage("engineImportUnknownComboRef", { row, ref }));
    }
  } else if (isFilled(rec.designation)) {
    Object.assign(l, resolveDesignation(String(rec.designation)));
  }

  // Explicit file name (wins over any free designation).
  if (isFilled(rec.fileName)) l.fileName = String(rec.fileName).trim();

  // Dimensions: a FILLED but unreadable cell (ref, text) is a reason, never a silence — that is the
  // signal catching a paste with shifted columns ("IMP-1401" under L). An empty cell says nothing
  // (the dimension is entered on the quote).
  /**
   * Reads one dimension cell into the line, or records why it is unreadable.
   * @param key The dimension column.
   */
  const readDimension = (key: "L" | "H") => {
    if (!isFilled(rec[key])) return;
    const d = dimension(rec[key]);
    if (d === undefined) {
      reasons.push(engineMessage("engineImportNotADimension", { value: String(rec[key]).trim(), column: key }));
      unreadableDimension = true;
    } else l[key] = d;
  };
  readDimension("L");
  readDimension("H");
  if (isFilled(rec.prodNote)) l.prodNote = String(rec.prodNote).trim();
  if (isFilled(rec.installNote)) l.installNote = String(rec.installNote).trim();

  // Material supply — supplier/brand are INFORMATIVE (taken verbatim, never validated against §17).
  if (isFilled(rec.supplier)) l.matSupplier = String(rec.supplier).trim();
  if (isFilled(rec.brand)) l.brand = String(rec.brand).trim();
  // "Material supplied" checkbox: an explicit column wins (recent, decoupled format); otherwise the
  // backward-compatible MIGRATION applies — a material supplier entered (the former trigger) ticks it.
  if (isFilled(rec.materialSupplied)) l.materialSupplied = isSuppliedChecked(rec.materialSupplied);
  else if (l.matSupplier) l.materialSupplied = true;

  // Gate check §17: items kept but FORBIDDEN for the material (never substituted).
  if (l.material) {
    /**
     * Records a reason when an item is forbidden for the line's material.
     * @param fieldLabel The i18n key of the field name shown in the reason.
     * @param field The compatibility field.
     * @param v The item ref.
     */
    const check = (fieldLabel: string, field: "print" | "cut" | "lamination" | "finishing1" | "finishing2", v?: string) => {
      if (v && !itemAllowed(field, l.material, v)) reasons.push(engineMessage("engineImportIncompatible", { item: v, field: keyParam(fieldLabel), material: String(l.material) }));
    };
    check("engineImportAxisPrint", "print", l.print);
    check("engineImportAxisCut", "cut", l.cut);
    check("engineImportAxisLamination", "lamination", l.lamination);
    check("engineImportAxisFinishing1", "finishing1", l.finishing1);
    check("engineImportAxisFinishing2", "finishing2", l.finishing2);
  }
  return { line: l, reasons, ignored, unreadableDimension };
}

/**
 * Is the record empty (none of the identifying columns filled)?
 * @param rec The record.
 * @returns True when nothing identifies a line.
 */
const isEmptyRecord = (rec: ImportRecord) =>
  (["fileName", "comboRef", "material", "print", "designation", "L", "H", "prodNote"] as ColumnKey[]).every((k) => !isFilled(rec[k]));

/**
 * Separator of a spreadsheet paste: tab, then ";", then ",".
 * @param text The pasted text.
 * @returns The separator.
 */
function detectSeparator(text: string): string {
  const first = text.split(/\r?\n/)[0] ?? "";
  if (first.includes("\t")) return "\t";
  if (first.includes(";")) return ";";
  return ",";
}

/**
 * Catalogue version carried by the paste (best-effort): "catalogue 14", "gabarit ... 14".
 * @param text The pasted text.
 * @returns The version, or null.
 */
function detectTemplateVersion(text: string): number | null {
  const m = text.match(/catalogue[^\d]{0,8}(\d{1,3})/i) ?? text.match(/gabarit[^\d]{0,16}(\d{1,3})/i);
  return m ? parseInt(m[1]!, 10) : null;
}

/**
 * Metadata row (version marker such as "Gabarit catalogue 14"): single cell, excluded from the table.
 * @param line The row.
 * @returns True when the row is a version marker.
 */
const isVersionMarker = (line: string) => !/[\t;,]/.test(line) && /(gabarit|catalogue)/i.test(line) && /\d/.test(line);

/**
 * Parses a spreadsheet paste.
 * @param text The pasted text.
 * @param templateVersion The template version detected in the paste.
 * @returns The import result.
 */
function parseSpreadsheet(text: string, templateVersion: number | null): ImportResult {
  const rows = text.split(/\r?\n/).map((r) => r.trim()).filter((r) => r !== "" && !isVersionMarker(r));
  if (rows.length === 0) return emptyResult(templateVersion);
  const separator = detectSeparator(rows.join("\n"));
  /**
   * Splits a row into trimmed cells.
   * @param r The row.
   * @returns The cells.
   */
  const split = (r: string) => r.split(separator).map((c) => c.trim());

  const header = split(rows[0]!);
  const normalisedHeader = header.map(norm);
  const mapped = normalisedHeader.map((c) => ALIAS[c]);
  const hasHeader = mapped.filter(Boolean).length >= 2;
  const columns: (ColumnKey | undefined)[] = hasHeader ? mapped : DEFAULT_COLUMN_ORDER.slice();
  const pricesIgnored = hasHeader && normalisedHeader.some((c) => PRICE_HEADERS.has(c));
  const body = hasHeader ? rows.slice(1) : rows;

  const acc = createAccumulator(templateVersion, pricesIgnored, hasHeader);
  body.forEach((row, i) => {
    const cells = split(row);
    const rec: ImportRecord = {};
    cells.forEach((val, c) => {
      const key = columns[c];
      if (key && val !== "") rec[key] = val;
    });
    if (isEmptyRecord(rec)) { acc.ignoredRows++; return; }
    acc.add(rec, i + 1);
  });
  return acc.finish("spreadsheet");
}

/**
 * Parses a JSON paste (an array of objects, an object, or `{ lines: [...] }` / `{ lignes: [...] }`).
 * @param text The pasted text.
 * @param templateVersion The template version detected in the paste.
 * @returns The import result.
 */
function parseJson(text: string, templateVersion: number | null): ImportResult {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch (e) {
    const r = emptyResult(templateVersion);
    return { ...r, format: "json", ignoredCells: [`JSON invalide : ${e instanceof Error ? e.message : String(e)}`] };
  }
  /**
   * The array under a wrapper key of the parsed JSON, if any.
   * @param key The wrapper key.
   * @returns The array, or null.
   */
  const wrapped = (key: "lines" | "lignes"): unknown[] | null =>
    data && typeof data === "object" && Array.isArray((data as Record<string, unknown>)[key])
      ? ((data as Record<string, unknown>)[key] as unknown[])
      : null;
  const arr: unknown[] = Array.isArray(data)
    ? data
    : wrapped("lines") ?? wrapped("lignes") ?? (data && typeof data === "object" ? [data] : []);
  const version = templateVersion ?? (data && typeof data === "object" ? Number((data as { version?: unknown }).version) || null : null);
  const acc = createAccumulator(version, false);
  arr.forEach((item, i) => {
    if (!item || typeof item !== "object") { acc.ignoredCells.push(engineMessage("engineImportEntryNotObject", { entry: i + 1 })); return; }
    const rec: ImportRecord = {};
    for (const [k, v] of Object.entries(item as Record<string, unknown>)) {
      const key = ALIAS[norm(k)];
      if (key) rec[key] = v;
      else if (PRICE_HEADERS.has(norm(k))) acc.pricesIgnored = true;
    }
    if (isEmptyRecord(rec) && rec.type == null) { acc.ignoredRows++; return; }
    acc.add(rec, i + 1);
  });
  return acc.finish("json");
}

/**
 * Shared accumulator (spreadsheet / JSON): aggregates the lines plus the three-category report.
 * `hasHeader` means the paste carried a recognised header row (always true in JSON, where keys name
 * the columns). A paste WITHOUT header and with an unreadable dimension means shifted columns, and we say so.
 * @param templateVersion The template version detected in the paste.
 * @param pricesIgnored Whether the paste contained price columns.
 * @param hasHeader Whether the paste carried a recognised header row.
 * @returns The accumulator.
 */
function createAccumulator(templateVersion: number | null, pricesIgnored: boolean, hasHeader = true) {
  const lines: ImportLine[] = [];
  const incomplete: IncompleteImportLine[] = [];
  const ignoredCells: string[] = [];
  let cleanCount = 0;
  let unreadableDimensions = 0;
  return {
    ignoredCells,
    ignoredRows: 0,
    pricesIgnored,
    /**
     * Adds one record to the report.
     * @param rec The record.
     * @param row The 1-based row number.
     */
    add(rec: ImportRecord, row: number) {
      const { line, reasons, ignored, unreadableDimension } = toLine(rec, row);
      lines.push(line);
      ignoredCells.push(...ignored);
      if (unreadableDimension) unreadableDimensions++;
      if (reasons.length) {
        const name = line.fileName || line.material || line.ref || `Ligne ${row}`;
        incomplete.push({ row, name, reasons });
      } else cleanCount++;
    },
    /**
     * Closes the accumulation into the final result.
     * @param format The source format.
     * @returns The import result.
     */
    finish(format: "spreadsheet" | "json"): ImportResult {
      const versionWarning = templateVersion != null && templateVersion < CATALOGUE_VERSION
        ? engineMessage("engineImportTemplateOutdated", { template: templateVersion, current: CATALOGUE_VERSION })
        : null;
      return { lines, cleanCount, incomplete, ignoredCells, pricesIgnored: this.pricesIgnored, format,
        ignoredRows: this.ignoredRows, templateVersion, versionWarning,
        headerWarning: !hasHeader && unreadableDimensions > 0 ? HEADER_MESSAGE : null };
    },
  };
}

/**
 * Result of an empty paste.
 * @param templateVersion The template version detected in the paste.
 * @returns The empty result.
 */
function emptyResult(templateVersion: number | null): ImportResult {
  return { lines: [], cleanCount: 0, incomplete: [], ignoredCells: [], pricesIgnored: false, format: "empty", ignoredRows: 0, templateVersion, versionWarning: null, headerWarning: null };
}

/**
 * Parses a paste into draft lines plus a report (clean / incomplete / ignored cells). Detects JSON vs
 * spreadsheet. NEVER produces a complete quote — only lines to add to the current one.
 * @param text The pasted text.
 * @returns The draft lines and the report.
 */
export function parseImportLines(text: string): ImportResult {
  const t = (text ?? "").trim();
  if (t === "") return emptyResult(null);
  const version = detectTemplateVersion(t);
  if (t.startsWith("[") || t.startsWith("{")) return parseJson(t, version);
  return parseSpreadsheet(t, version);
}
